import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Space,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText, formatDate } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ListResponse,
  NewsCircumscriptionRow,
  NewsMutationResponse,
  NewsRow,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

/** The date format the API parses (ApplicationHelper.date_or_nil). */
const API_DATE = "YYYY-MM-DD";

interface NewsFormValues {
  date_from: dayjs.Dayjs;
  date_to: dayjs.Dayjs;
  content: string;
}

/**
 * News, in the two scopes the sidebar offers:
 *
 * - `webmaster` — site-wide news (route /news/site_news). Only these can be
 *   published to individual circumscriptions, which is what the drawer does.
 * - `zone_coordinator` — the news of the own circumscription
 *   (route /news/circumscription_news), which the server attaches to that
 *   circumscription on creation.
 *
 * One component for both: the list, the editor and the delete are identical,
 * and the endpoints differ only in their path. Splitting them into two screens
 * would mean maintaining the same form twice.
 */
export function NewsPage({ scope }: { scope: "webmaster" | "zone_coordinator" }) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<NewsFormValues>();

  // null = closed, "new" = create, a row = edit.
  const [editing, setEditing] = useState<NewsRow | "new" | null>(null);
  const [publishing, setPublishing] = useState<NewsRow | null>(null);

  const listPath = scope === "webmaster" ? "/news/webmaster" : "/news/zone_coordinator";

  const news = useQuery({
    queryKey: ["news", scope],
    queryFn: () => apiFetch<ListResponse<NewsRow>>(listPath),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refreshList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["news", scope] });
  }, [queryClient, scope]);

  const save = useMutation({
    mutationFn: (values: NewsFormValues) => {
      const body = {
        date_from: values.date_from.format(API_DATE),
        date_to: values.date_to.format(API_DATE),
        content: values.content,
      };

      return editing === "new" || editing === null
        ? apiFetch<NewsMutationResponse>(listPath, { method: "POST", body })
        : apiFetch<NewsMutationResponse>(`/news/${editing.id}`, { method: "PUT", body });
    },
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refreshList();
    },
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/news/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refreshList();
    },
    onError: showError,
  });

  const askAndRemove = useCallback(
    (row: NewsRow) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("dialog.confirm_delete", { name: row.content }),
        okText: t("dialog.confirm"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.cancel"),
        onOk: () => remove.mutate(row.id),
      });
    },
    [modal, t, remove],
  );

  const openEditor = useCallback(
    (row: NewsRow | "new") => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      if (row !== "new") {
        form.setFieldsValue({
          date_from: row.date_from ? dayjs(row.date_from) : undefined,
          date_to: row.date_to ? dayjs(row.date_to) : undefined,
          content: row.content ?? "",
        } as NewsFormValues);
      }
    },
    [form],
  );

  const columns = useMemo<TableProps<NewsRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.news.role_id"),
        dataIndex: "author",
        width: 180,
        sorter: (a, b) => compareText(a.author, b.author),
        // The old grid set the author bold and added a green icon for an item
        // that changed since the last login; the tooltip carries the same word.
        render: (author: string, row) =>
          row.modified ? (
            <Tooltip title={t("icon.modified")}>
              <Typography.Text strong>{author}</Typography.Text>
            </Tooltip>
          ) : (
            author
          ),
      },
      {
        title: t("activerecord.attributes.news.date_from"),
        dataIndex: "date_from",
        width: 130,
        defaultSortOrder: "descend",
        sorter: (a, b) => compareText(a.date_from, b.date_from),
        render: (value: string | null) => formatDate(value),
      },
      {
        title: t("activerecord.attributes.news.date_to"),
        dataIndex: "date_to",
        width: 130,
        sorter: (a, b) => compareText(a.date_to, b.date_to),
        render: (value: string | null) => formatDate(value),
      },
      {
        title: t("activerecord.attributes.news.content"),
        dataIndex: "content",
      },
      {
        key: "actions",
        fixed: "right",
        width: scope === "webmaster" ? 150 : 100,
        render: (_value, row) => (
          <Space>
            <Tooltip title={t("form.update")}>
              <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(row)} />
            </Tooltip>
            {scope === "webmaster" && (
              // Only site news is published per circumscription; a zone
              // coordinator's item already belongs to exactly one.
              <Tooltip title={t("activerecord.models.circumscription")}>
                <Button size="small" icon={<GlobalOutlined />} onClick={() => setPublishing(row)} />
              </Tooltip>
            )}
            <Tooltip title={t("news.remove")}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => askAndRemove(row)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [t, scope, openEditor, askAndRemove],
  );

  return (
    <Card
      title={t("news.news_header")}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor("new")}>
          {t("news.create")}
        </Button>
      }
    >
      <DataTable<NewsRow>
        columns={columns}
        rows={news.data?.data}
        loading={news.isPending || remove.isPending}
        error={news.error}
        exportAs={t("news.news_header")}
      />

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("news.create") : t("news.news_header")}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
        width={640}
      >
        <Form<NewsFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Space size="middle">
            <Form.Item
              name="date_from"
              label={t("activerecord.attributes.news.date_from")}
              rules={[{ required: true }]}
            >
              <DatePicker />
            </Form.Item>

            <Form.Item
              name="date_to"
              label={t("activerecord.attributes.news.date_to")}
              dependencies={["date_from"]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  // The model validates date_from on_or_before date_to; saying
                  // so here saves a round trip for the obvious mistake.
                  validator: (_rule, value: dayjs.Dayjs) => {
                    const from = getFieldValue("date_from") as dayjs.Dayjs | undefined;
                    return !value || !from || !value.isBefore(from, "day")
                      ? Promise.resolve()
                      : Promise.reject(new Error(t("activerecord.attributes.news.date_to")));
                  },
                }),
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Space>

          <Form.Item
            name="content"
            label={t("activerecord.attributes.news.content")}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>
        </Form>
      </Modal>

      {publishing && (
        <PublishDrawer news={publishing} onClose={() => setPublishing(null)} />
      )}
    </Card>
  );
}

/**
 * Which circumscriptions a site news item is published to. Every tick is one
 * request — the endpoints take a single circumscription in the path, and the
 * list is a few dozen rows at most.
 */
function PublishDrawer({ news, onClose }: { news: NewsRow; onClose: () => void }) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const circumscriptions = useQuery({
    queryKey: ["news", "circumscriptions", news.id],
    queryFn: () => apiFetch<ListResponse<NewsCircumscriptionRow>>(`/news/${news.id}/circumscriptions`),
  });

  const toggle = useMutation({
    mutationFn: ({ id, selected }: { id: number; selected: boolean }) =>
      apiFetch<NotificationsResponse>(`/news/${news.id}/circumscriptions/${id}`, {
        method: selected ? "PUT" : "DELETE",
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey: ["news", "circumscriptions", news.id] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const rows = circumscriptions.data?.data;
  const selectedKeys = useMemo(
    () => (rows ?? []).filter((row) => row.selected).map((row) => row.id),
    [rows],
  );

  const columns = useMemo<ExportableColumn<NewsCircumscriptionRow>[]>(
    () => [
      {
        // Whether the item is published here is the tick box on the left, which
        // is a control and not a column — so the export would lose exactly the
        // state the list is about. Hidden in the table, present in the file.
        title: t("activerecord.attributes.circumscription_news.select"),
        dataIndex: "selected",
        hidden: true,
      },
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 100,
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
      },
      {
        title: t("activerecord.attributes.circumscription.denomination"),
        dataIndex: "denomination",
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.circumscription.country"),
        dataIndex: "country",
        width: 120,
      },
      {
        title: t("activerecord.attributes.language.language_code"),
        dataIndex: "language_code",
        width: 90,
      },
      {
        // The list holds deactivated circumscriptions too — what was published
        // to one stays published — and until now nothing on the screen said
        // which ones those are.
        title: t("activerecord.attributes.circumscription.active"),
        dataIndex: "active",
        width: 90,
        align: "center",
        sorter: (a, b) => Number(a.active) - Number(b.active),
        render: (value: boolean) =>
          value ? <CheckOutlined /> : <Typography.Text type="secondary">—</Typography.Text>,
      },
    ],
    [t],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={760}
      title={t("activerecord.models.circumscription")}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{news.content}</Typography.Paragraph>

      <DataTable<NewsCircumscriptionRow>
        columns={columns}
        rows={rows}
        loading={circumscriptions.isPending || toggle.isPending}
        error={circumscriptions.error}
        exportAs={t("activerecord.models.circumscription")}
        paginated={false}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          // No select-all: it would fire one request per circumscription.
          hideSelectAll: true,
          onSelect: (record, selected) => toggle.mutate({ id: record.id, selected }),
        }}
      />
    </Drawer>
  );
}
